import { useState, useMemo } from "react";
import { Link } from "wouter";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown, X } from "lucide-react";
import { useSEO } from "@/hooks/use-seo";
import { useLanguage } from "@/contexts/LanguageContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ExpertContact from "@/components/ExpertContact";
import { cases, verticals, serviceFilters } from "@/data/cases";
import { HeroBackground } from "@/components/HeroBackground";

export default function Cases() {
  const { t, language, localePath } = useLanguage();
  const nl = language === "nl";
  const [activeVertical, setActiveVertical] = useState(0);
  const [activeService, setActiveService] = useState(0);

  useSEO({
    title: nl
      ? "Cases & Resultaten | Wrkt Digital - D2C E-commerce Marketing Resultaten"
      : "Case Studies & Results | Wrkt Digital - D2C E-commerce Marketing Results",
    description: nl
      ? "Bekijk bewezen resultaten van Wrkt Digital voor D2C e-commerce merken. Cases van Vedder & Vedder, Franky Amsterdam, Zèvy, Pig & Hen en meer. Meta Ads, TikTok, Google Ads & Klaviyo strategieën met meetbare groei. Digital marketing bureau Amsterdam."
      : "See proven results from Wrkt Digital for D2C e-commerce brands. Case studies from Vedder & Vedder, Franky Amsterdam, Zèvy, Pig & Hen and more. Meta Ads, TikTok, Google Ads & Klaviyo strategies with measurable growth. Digital marketing agency Amsterdam.",
    path: "/cases",
    lang: nl ? "nl" : "en",
    keywords: nl
      ? "marketing cases, e-commerce resultaten, D2C marketing cases, digital marketing resultaten, Meta Ads cases, TikTok advertising resultaten, ROAS resultaten, omzetgroei e-commerce, performance marketing cases, online marketing bureau resultaten Amsterdam"
      : "marketing case studies, e-commerce results, D2C marketing cases, digital marketing results, Meta Ads case studies, TikTok advertising results, ROAS results, e-commerce revenue growth, performance marketing cases, online marketing agency results Amsterdam",
  });

  const verticalLabels = nl ? verticals.nl : verticals.en;
  const serviceLabels = nl ? serviceFilters.nl : serviceFilters.en;

  const verticalMap: Record<string, string> = {
    "Fashion & Jewelry": "Fashion & Sieraden",
    "Fashion & Sieraden": "Fashion & Sieraden",
    "Beauty & Care": "Beauty & Verzorging",
    "Beauty & Verzorging": "Beauty & Verzorging",
    "Home & Living": "Home & Living",
    "Mobility": "Mobiliteit",
    "Mobiliteit": "Mobiliteit",
    "Entertainment": "Entertainment",
    "Education": "Educatie",
    "Educatie": "Educatie",
  };

  const filteredCases = useMemo(() => {
    return cases.filter((c) => {
      const selectedVertical = verticalLabels[activeVertical];
      const selectedService = serviceLabels[activeService];

      const verticalMatch =
        activeVertical === 0 ||
        c.vertical === selectedVertical ||
        c.vertical === verticalMap[selectedVertical];

      const serviceMatch =
        activeService === 0 || c.services.includes(selectedService);

      return verticalMatch && serviceMatch;
    });
  }, [activeVertical, activeService, nl]);

  return (
    <div className="min-h-screen bg-[#0A1F14] text-white relative">


      <style>{`
        @keyframes color-pulse-shift {
          0% { background-color: rgba(141, 24, 43, 0.5); transform: scale(1); opacity: 0.5; }
          50% { background-color: rgba(76, 234, 172, 0.2); transform: scale(1.04); opacity: 0.35; }
          100% { background-color: rgba(141, 24, 43, 0.5); transform: scale(1); opacity: 0.5; }
        }
      `}</style>

      <div className="relative z-10">
      <Navbar activePage="cases" />
      <section className="relative pt-32 pb-16 md:pt-36 md:pb-20 overflow-hidden bg-grad-hero has-grain breathing">

        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <div className="pill pill--brand pill--lg mb-6 uppercase tracking-widest">
              <span>{nl ? "Ons werk" : "Our work"}</span>
            </div>
            <h1 className="text-5xl md:text-[68px] font-display font-bold leading-[1.02] tracking-[-0.03em] mb-6">
              {nl ? "Merken die " : "Brands that "}
              <span className="text-[#4CEAAC]" style={{ textShadow: "0 0 30px rgba(76,234,172,0.3)" }}>
                {nl ? "groeien" : "grow"}
              </span>
              {nl ? " met Wrkt." : " with Wrkt."}
            </h1>
            <p className="text-lg md:text-xl text-white/70 leading-relaxed max-w-2xl">
              {nl
                ? "Van startup tot scale-up - ontdek hoe wij D2C merken helpen om structureel te groeien met data-gedreven marketing."
                : "From startup to scale-up - discover how we help D2C brands achieve structural growth with data-driven marketing."}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-4 md:py-5">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {verticalLabels.map((label, i) => (
                <button
                  key={label}
                  onClick={() => setActiveVertical(i)}
                  data-testid={`filter-vertical-${i}`}
                  className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                    activeVertical === i
                      ? 'border-[#4CEAAC]/60 bg-[#4CEAAC]/10 text-[#4CEAAC]'
                      : 'border-white/10 bg-white/[0.04] text-white/70 hover:border-white/25 hover:text-white'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="relative">
              <select
                value={activeService}
                onChange={(e) => setActiveService(Number(e.target.value))}
                data-testid="filter-service"
                className="appearance-none bg-white/[0.06] border border-white/10 text-white text-sm font-medium rounded-full pl-5 pr-10 py-2.5 cursor-pointer hover:border-white/20 focus:border-[#4CEAAC]/40 focus:outline-none transition-colors"
              >
                {serviceLabels.map((label, i) => (
                  <option key={label} value={i} className="bg-[#0A1F14] text-white">
                    {label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
            </div>

            {(activeVertical !== 0 || activeService !== 0) && (
              <button
                onClick={() => { setActiveVertical(0); setActiveService(0); }}
                data-testid="button-clear-filters"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-white/40 hover:text-white/70 transition-colors pl-2"
              >
                <X className="w-3.5 h-3.5" />
                {nl ? "Wis filters" : "Clear filters"}
              </button>
            )}

            <span className="text-xs text-white/30 ml-auto">
              {filteredCases.length} {filteredCases.length === 1 ? "case" : "cases"}
            </span>
          </div>
        </div>
      </section>

      <section className="py-8 md:py-12">
        <div className="container mx-auto px-6 md:px-12">
          {filteredCases.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-white/40 text-lg">
                {nl
                  ? "Geen cases gevonden met deze filters."
                  : "No cases found with these filters."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredCases.map((c, i) => (
                <motion.div
                  key={c.slug}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08, type: 'spring', stiffness: 120 }}
                >
                  <Link href={localePath(`/cases/${c.slug}`)} data-testid={`card-case-${c.slug}`}>
                    <div className="group relative bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden hover:border-[#4CEAAC]/30 hover:bg-[#4CEAAC]/[0.04] transition-all duration-500 hover:shadow-[0_0_50px_rgba(76,234,172,0.12),_0_0_20px_rgba(76,234,172,0.06)] hover:-translate-y-1 cursor-pointer h-full flex flex-col">
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img
                          src={c.heroImage}
                          alt={`${c.name} case Wrkt Digital${c.results?.[0] ? ` – ${c.results[0].value} ${c.results[0].label[nl ? "nl" : "en"]}` : ""}`}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          style={c.imagePosition ? { objectPosition: c.imagePosition } : undefined}
                          loading="lazy"
                          decoding="async"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F14] via-transparent to-transparent opacity-60" />
                        <div className="absolute bottom-4 left-4 flex gap-2 flex-wrap">
                          {c.services.slice(0, 3).map((s) => (
                            <span
                              key={s}
                              className="px-2.5 py-1 bg-[#0A1F14]/80 backdrop-blur-sm border border-white/10 rounded-full text-[10px] font-semibold uppercase tracking-wider text-white/70 group-hover:border-[#4CEAAC]/20 group-hover:text-white/90 transition-all duration-500"
                            >
                              {s}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="p-6 flex flex-col flex-1">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-[10px] font-semibold uppercase tracking-widest text-[#4CEAAC]/70">
                            {c.vertical}
                          </span>
                        </div>
                        <h3 className="text-xl font-display font-bold mb-2 group-hover:text-[#4CEAAC] transition-colors">
                          {c.name}
                        </h3>
                        <p className="text-sm text-white/50 leading-relaxed flex-1">
                          {nl ? c.tagline.nl : c.tagline.en}
                        </p>
                      </div>

                      <div className="px-6 pb-5 pt-0">
                        <div className="flex items-center gap-2 text-sm font-semibold text-[#4CEAAC] group-hover:gap-3 transition-all">
                          {nl ? "Bekijk case" : "View case"}
                          <ArrowRight className="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="relative bg-grad-duo has-grain py-[120px] px-6 md:px-10 overflow-hidden">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }} className="relative max-w-[900px] mx-auto text-center">
          <p className="text-[11.5px] font-semibold uppercase tracking-[0.2em] text-white/70">
            {nl
              ? 'Dit zijn de resultaten. De eerste stap? Een gratis Growth Scan.'
              : 'These are the results. The first step? A free Growth Scan.'}
          </p>
          <h2 className="font-display font-extrabold text-[clamp(40px,6.2vw,80px)] tracking-[-0.04em] leading-[0.98] mt-5">
            {nl ? 'Klaar om te ' : 'Ready to '}
            <span className="bg-[linear-gradient(92deg,#4BF4B9,#86F0B4)] bg-clip-text text-transparent">{nl ? 'groeien' : 'grow'}</span>
            ?
          </h2>
          <p className="text-[19px] leading-relaxed text-white/[0.78] max-w-[48ch] mx-auto mt-6">
            {nl
              ? 'Ontdek wat Wrkt Digital voor jouw merk kan betekenen. Start met een gratis Growth Scan. Vrijblijvend, met een concreet beeld van je grootste kansen.'
              : 'Discover what Wrkt Digital can do for your brand. Start with a free Growth Scan. No strings attached, with a concrete view of your biggest opportunities.'}
          </p>
          <div className="flex gap-3.5 mt-10 justify-center flex-wrap">
            <Link href={localePath('/scan')} className="btn btn--gradient btn--lg" data-testid="button-cases-cta">
              {nl ? 'Start je gratis Growth Scan →' : 'Start your free Growth Scan →'}
            </Link>
            <Link href={localePath('/contact')} className="btn btn--lg btn--outline-inverse" data-testid="button-cases-intro">
              {nl ? 'Plan een kennismaking →' : 'Book an intro call →'}
            </Link>
          </div>
        </motion.div>
      </section>

      <ExpertContact />

      <Footer />
      </div>
    </div>
  );
}
